import {BaseApiService} from "../../shared/utils/base-api-service";
import {from, Observable, throwError, of, tap, EMPTY, Subject, filter, take} from "rxjs";
import {switchMap, catchError} from 'rxjs/operators';
import {NotificationSubResponseDto} from "../dto/notification-subscription-dto";
import {NotificationSubRequestDto} from "../dto/notification-sub-request-dto";
import {getToken, Messaging, onMessage} from "@angular/fire/messaging";
import {environment} from "../../../environments/environment";
import {ApplicationRef, Injectable} from "@angular/core";
import {AuthenticationManager} from "../../shared/services/authentication-manager";
import {LocalizerService} from "../../shared";
import {IncomingCallNotification} from "../models/incoming-call-notification";
import {CallEntityKind} from "../../shared/models/webrtc/call/v2/call-entity";
import {CallUpdateState} from "../../shared/models/webrtc/call/v2/call-update";

/** Max age (in seconds) for a notification to be considered valid. Older notifications are ignored. */
const STALE_NOTIFICATION_THRESHOLD_SECONDS = 15;

@Injectable()
export class NotificationsManagerService extends BaseApiService {

    private _incomingCallNotification$: Subject<IncomingCallNotification> = new Subject<IncomingCallNotification>();

    constructor(private messaging: Messaging, private auth: AuthenticationManager,
                private localizer: LocalizerService, private appRef: ApplicationRef) {
        super();
        this.auth.userLoggedInUpdates().subscribe(
            (loggedIn) => {
                if (loggedIn) {
                    this.initNotifications();
                }
            }
        );
    }

    setupListener(): void {
        if (!this.messaging) {
            console.warn('[Notifications] Firebase Messaging not available, skipping foreground listener');
            return;
        }

        onMessage(this.messaging, (payload) => {
            this.handleForegroundMessage(payload);
        });

        // Listen for messages sent from the service worker.
        // These messages are received when the user focuses the tab by clicking on a push notification received while
        // the app was in the background.
        if (navigator.serviceWorker) {
            navigator.serviceWorker.addEventListener('message', (event) => {
                const payload = event.data;

                // Only process messages that match our call data format.
                // All other SW messages (Firebase SDK internal, etc.) are silently ignored.
                console.log('[Notifications] SW message received | keys:', payload ? Object.keys(payload).join(',') : 'null',
                    '| id:', payload?.id || payload?.call_id,
                    '| state:', payload?.state || payload?.status,
                    '| timestamp:', payload?.timestamp,
                    '| has caller:', !!payload?.caller, '| has call_id:', !!payload?.call_id);

                if (payload && payload.caller && payload.callee) {
                    if (this.isStaleNotification(payload)) return;
                    console.log('[Notifications] BACKGROUND click → V2 call | id:', payload.id, '| state:', payload.state);
                    this._incomingCallNotification$.next(payload);
                } else if (payload && payload.call_id && payload.device_id) {
                    if (this.isStaleNotification(payload)) return;
                    const normalized = this.normalizeV1Message(payload);
                    if (normalized) {
                        console.log('[Notifications] BACKGROUND click → V1 call | id:', normalized.id, '| state:', normalized.state);
                        this._incomingCallNotification$.next(normalized);
                    }
                }
            });
        }
    }

    onIncomingCallNotification = (): Observable<IncomingCallNotification> =>
        this._incomingCallNotification$.asObservable();

    private handleForegroundMessage(payload: any): void {
        if (!payload || !payload.data || typeof payload.data.message !== 'string') {
            return;
        }

        let message: any;
        try {
            message = JSON.parse(payload.data.message);
        } catch (err) {
            return;
        }

        if (!message) {
            return;
        }

        console.log('[Notifications] handleForegroundMessage | id:', message.id || message.call_id,
            '| state:', message.state || message.status,
            '| timestamp:', message.timestamp, '| has caller:', !!message.caller,
            '| has call_id:', !!message.call_id);

        // Ignore stale notifications to avoid showing incoming-call-card for old calls
        if (this.isStaleNotification(message)) {
            return;
        }

        // If it's a call notification, emit to subscribers
        if (message.caller && message.callee) {
            console.log('[Notifications] FOREGROUND V2 call | id:', message.id, '| state:', message.state);
            this._incomingCallNotification$.next(message);
            return;
        }

        // V1 message: normalize and emit
        const v1Normalized = this.normalizeV1Message(message);
        if (v1Normalized) {
            console.log('[Notifications] FOREGROUND V1 call | id:', v1Normalized.id, '| state:', v1Normalized.state);
            this._incomingCallNotification$.next(v1Normalized);
            return;
        }
    }

    /**
     * Initializes push notifications.
     *
     * SAFARI COMPATIBILITY NOTE:
     * Safari requires that Notification.requestPermission() is called from a user gesture
     * (click, tap, etc.). Calling it programmatically will fail with:
     * "Notification prompting can only be done from a user gesture."
     *
     * For this reason, we check if permission is already granted before proceeding.
     * If permission is 'default' (not yet requested), we skip automatic initialization.
     * Use requestNotificationPermission() from a user-triggered event (button click) instead.
     */
    private initNotifications(): void {
        // Check if Notification API is available
        if (typeof Notification === 'undefined') {
            console.warn('Notification API not available');
            return;
        }

        // Safari fix: Only proceed if permission is already granted.
        // If permission is 'default', the user must click a button to request permission.
        // Calling requestPermission() without a user gesture fails on Safari.
        if (Notification.permission === 'default') {
            console.info('Notification permission not yet requested. User must grant permission via UI action.');
            return;
        }

        if (Notification.permission !== 'granted') {
            console.info('Notification permission denied');
            return;
        }

        // Permission is already granted, proceed with registration
        this.registerAndSubscribe();
    }

    /**
     * Request notification permission from user.
     * MUST be called from a user gesture (click handler) for Safari compatibility.
     */
    requestNotificationPermission(): Observable<boolean> {
        if (typeof Notification === 'undefined') {
            return of(false);
        }

        // If already granted, just register
        if (Notification.permission === 'granted') {
            this.registerAndSubscribe();
            return of(true);
        }

        // If denied, can't request again
        if (Notification.permission === 'denied') {
            return of(false);
        }

        // Request permission (must be from user gesture on Safari)
        return from(Notification.requestPermission()).pipe(
            switchMap(permission => {
                if (permission === 'granted') {
                    this.registerAndSubscribe();
                    return of(true);
                }
                return of(false);
            }),
            catchError(err => {
                console.error('Error requesting notification permission', err);
                return of(false);
            })
        );
    }

    /**
     * Complete notification setup (SW registration + token + backend subscribe)
     * after permission has already been granted.
     * Called by components that handle Notification.requestPermission() directly
     * to preserve the user gesture context (required by Edge/Safari).
     */
    completeNotificationSetup(): void {
        this.registerAndSubscribe();
    }

    /**
     * Register service worker and subscribe to push notifications.
     * Called after permission is confirmed granted.
     */
    private registerAndSubscribe(): void {
        from(navigator.serviceWorker.register(environment.serviceWorkerFile)).pipe(
            switchMap(registration =>
                from(this.waitForServiceWorkerActivation(registration))
            ),
            switchMap(registration =>
                from(getToken(this.messaging, {
                    vapidKey: environment.vapidKey,
                    serviceWorkerRegistration: registration
                }))
            ),
            switchMap(token => token ? this.subscribeToNotifications(token) : EMPTY),
            catchError(err => {
                console.error('Error while initializing notifications', err);
                return EMPTY;
            })
        ).subscribe();
    }

    private waitForServiceWorkerActivation(
        registration: ServiceWorkerRegistration
    ): Promise<ServiceWorkerRegistration> {
        if (registration.active) {
            return Promise.resolve(registration);
        }

        return new Promise(resolve => {
            const sw = registration.installing || registration.waiting;
            if (!sw) {
                resolve(registration);
                return;
            }

            sw.addEventListener('statechange', () => {
                if (sw.state === 'activated') {
                    resolve(registration);
                }
            });
        });
    }

    private subscribeToNotifications(token: string): Observable<NotificationSubResponseDto> {
        if (!token) {
            return throwError(() => new Error("Invalid token"));
        }

        if (localStorage.getItem("TOKEN") === token &&
            localStorage.getItem("SUBSCRIPTION_ARN") != null &&
            localStorage.getItem("ENDPOINT_ARN") != null) {
            return EMPTY;
        }

        this.clearSubscription();

        const body: NotificationSubRequestDto = {
            device_token: token,
            platform: 0
        }

        return this.post<NotificationSubResponseDto>("/notifications/subscribe", body).pipe(
            tap(sub => this.storeSubscription(sub, token))
        );
    }

    unsubscribeFromNotifications(): Observable<any> {
        const endpointArn = localStorage.getItem("ENDPOINT_ARN");
        const subscriptionArn = localStorage.getItem("SUBSCRIPTION_ARN");

        if (!endpointArn || !subscriptionArn) {
            this.clearSubscription();
            return of(null);
        }

        return this.post("/notifications/unsubscribe", {
            endpoint_arn: endpointArn,
            subscription_arn: subscriptionArn
        } as NotificationSubResponseDto).pipe(
            tap(() => {
                this.clearSubscription();
            })
        );
    }

    private storeSubscription(sub: NotificationSubResponseDto, token: string): void {
        localStorage.setItem("ENDPOINT_ARN", sub.endpoint_arn);
        localStorage.setItem("SUBSCRIPTION_ARN", sub.subscription_arn);
        localStorage.setItem("TOKEN", token);
    }

    private clearSubscription(): void {
        localStorage.removeItem("ENDPOINT_ARN");
        localStorage.removeItem("SUBSCRIPTION_ARN");
        localStorage.removeItem("TOKEN");
    }

    private normalizeV1Message(message: any): IncomingCallNotification | null {
        if (!message.call_id || !message.device_id) return null;

        // const destinationId = message.unit_id || message.pbx_id;
        // const destinationKind: CallEntityKind = message.unit_id
        //     ? CallEntityKind.Unit : CallEntityKind.PBX;
        const calleeId = message.unit_id;
        const calleeKind: CallEntityKind = message.unit_id == message.pbx__id ? CallEntityKind.PBX : CallEntityKind.Unit;
        const destinationId = message.pbx_id || message.unit_id;
        const destinationKind: CallEntityKind = message.pbx_id
            ? CallEntityKind.PBX : CallEntityKind.Unit;

        return {
            id: message.call_id,
            state: this.mapV1StatusToState(message.status),
            installation_id: message.installation_id,
            caller: { id: message.device_id, kind: CallEntityKind.Doorstation },
            callee: { id: calleeId, kind: calleeKind },
            source: { id: message.device_id, kind: CallEntityKind.Doorstation },
            destination: { id: destinationId, kind: destinationKind },
            version: 'v1',
        };
    }

    private isStaleNotification(message: any): boolean {
        const id = message.id || message.call_id || '?';
        const nowSeconds = Math.floor(Date.now() / 1000);

        if (!message.timestamp) {
            console.warn('[Notifications] isStaleNotification | id:', id,
                '| NO TIMESTAMP in message → treating as NOT stale | state:', message.state,
                '| keys:', Object.keys(message).join(','),
                '| now:', nowSeconds, '| Date.now():', Date.now());
            return false;
        }

        const ageSeconds = nowSeconds - message.timestamp;
        const isStale = ageSeconds > STALE_NOTIFICATION_THRESHOLD_SECONDS;

        console.log('[Notifications] isStaleNotification | id:', id,
            '| timestamp:', message.timestamp, '| now:', nowSeconds,
            '| age:', ageSeconds, 's | threshold:', STALE_NOTIFICATION_THRESHOLD_SECONDS, 's',
            '| stale:', isStale, '| state:', message.state);

        return isStale;
    }

    // V1 status enum: 1=IN_PROGRESS, 2=MISSED, 3=ANSWERED, 4=CANCELED
    private mapV1StatusToState(status: number): CallUpdateState {
        switch (status) {
            case 1:  return CallUpdateState.Started;
            case 2:  return CallUpdateState.Missed;
            case 3:  return CallUpdateState.Answered;
            case 4:  return CallUpdateState.Canceled;
            default: return CallUpdateState.Started;
        }
    }

}
